import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import type { AppealDebtCaseResponse, DebtCase, DebtCaseList } from "../../api/types";
import { CommandError } from "../../components/CommandError";
import { CursorListView } from "../../components/CursorListView";
import { Banner, Button, Card, EmptyState, Money, PageHeader, QueryView, StatusPill, Table, Textarea, type Tone } from "../../components/ui";
import { isZeroDecimal } from "../../lib/money";
import { formatLocal } from "../../lib/datetime";
import { useCursorList } from "../../lib/useCursorList";
import { useIntentKey } from "../../lib/useIntentKey";

// UX-DESIGN.md §3.4 "publisher debt notice": a dedicated case screen, not
// a buried ledger line (G-44, migration 0057):
//   GET /v1/debt-cases, GET /v1/debt-cases/{id} (publisher_reads.go:1168-1230)
//   POST /v1/debt-cases/{id}/appeal {reason, evidence?} (migration 0073) —
//   self-service, reusing 0036's staff-filed appeal's state machine, so a
//   staff decision on it works exactly as before.
// Staff-only fields (the advertiser, reservation and posting ids, reviewer
// notes, evidence bodies) are never returned.

export const DEBT_STATUS: Record<DebtCase["status"], { tone: Tone; label: string }> = {
  under_review: { tone: "warning", label: "Under review" },
  upheld: { tone: "error", label: "Upheld — amount owed" },
  not_upheld: { tone: "success", label: "Not upheld — nothing owed" },
  appeal_under_review: { tone: "warning", label: "Appeal under review" },
  appeal_upheld: { tone: "success", label: "Appeal upheld" },
  appeal_denied: { tone: "error", label: "Appeal denied" },
};

export function DebtCasesPage() {
  const ep = useEndpoints();
  const list = useCursorList<DebtCaseList, DebtCase>(["debt-cases"], (cursor) => ep.publisher.listDebtCases(cursor), (p) => p.debt_cases);
  return (
    <div className="stack-lg">
      <PageHeader
        title="Amounts owed"
        back={{ to: "/publisher/earnings", label: "Earnings" }}
        description="Cases where a delivery your payout had already claimed was later found invalid by a safety review."
      />
      <Card title="Cases">
        <CursorListView list={list} loadingLabel="Loading cases" empty={<EmptyState icon="scale" title="No cases">Nothing is owed to the platform.</EmptyState>}>
          {(rows) => (
            <Table
              caption="Debt cases, newest first"
              captionHidden
              rows={rows}
              rowKey={(c) => c.case_id}
              columns={[
                { key: "opened", header: "Opened", cell: (c) => <Link to={`/publisher/debt-cases/${c.case_id}`}>{formatLocal(c.opened_at)}</Link> },
                { key: "amt", header: "Amount", cell: (c) => <Money value={c.amount} currency={c.currency} />, numeric: true },
                {
                  key: "status",
                  header: "Status",
                  cell: (c) => {
                    const s = DEBT_STATUS[c.status] ?? { tone: "neutral" as Tone, label: c.status };
                    return <StatusPill tone={s.tone}>{s.label}</StatusPill>;
                  },
                },
              ]}
            />
          )}
        </CursorListView>
      </Card>
    </div>
  );
}

export function DebtCaseDetailPage() {
  const { caseId = "" } = useParams();
  const ep = useEndpoints();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["debt-case", caseId], queryFn: () => ep.publisher.getDebtCase(caseId) });
  return (
    <div className="stack-lg">
      <PageHeader title="Debt case" back={{ to: "/publisher/debt-cases", label: "Amounts owed" }} description={<code>{caseId}</code>} />
      <QueryView query={q} loadingLabel="Loading case">
        {(c: DebtCase) => {
          const s = DEBT_STATUS[c.status] ?? { tone: "neutral" as Tone, label: c.status };
          return (
            <>
              <Card title="Summary">
                <div className="stack">
                  <StatusPill tone={s.tone}>{s.label}</StatusPill>
                  <dl className="definition-list">
                    <dt>Amount</dt>
                    <dd>
                      <Money value={c.amount} currency={c.currency} />
                    </dd>
                    <dt>Why</dt>
                    <dd>
                      A safety review found a delivery invalid after a payout had already claimed its earnings (evidence category:{" "}
                      {c.summary.evidence_category === "safety_review" ? "safety review" : c.summary.evidence_category}).
                    </dd>
                    <dt>Site</dt>
                    <dd>
                      <Link to={`/publisher/sites/${c.summary.site_id}`}>
                        <code>{c.summary.site_id.slice(0, 8)}</code>
                      </Link>
                    </dd>
                    {c.summary.delivery_captured_at ? (
                      <>
                        <dt>Delivery captured</dt>
                        <dd>{formatLocal(c.summary.delivery_captured_at)}</dd>
                      </>
                    ) : null}
                    <dt>Opened</dt>
                    <dd>{formatLocal(c.opened_at)}</dd>
                    {c.resolved_at ? (
                      <>
                        <dt>Decided</dt>
                        <dd>{formatLocal(c.resolved_at)}</dd>
                      </>
                    ) : null}
                    {c.payout_ids.length ? (
                      <>
                        <dt>Payouts</dt>
                        <dd>
                          {c.payout_ids.map((id, i) => (
                            <span key={id}>
                              {i ? ", " : ""}
                              <Link to={`/publisher/payouts/${id}`}>
                                <code>{id.slice(0, 8)}</code>
                              </Link>
                            </span>
                          ))}
                        </dd>
                      </>
                    ) : null}
                  </dl>
                </div>
              </Card>
              {c.outcome ? (
                <Card title="Outcome">
                  <dl className="definition-list">
                    <dt>Recognized as owed</dt>
                    <dd>
                      <Money value={c.outcome.debt_recognized} currency={c.currency} /> <span className="xs muted">the payout had already paid out</span>
                    </dd>
                    <dt>Taken back before payment</dt>
                    <dd>
                      <Money value={c.outcome.clawed_back} currency={c.currency} /> <span className="xs muted">nothing is owed for this part</span>
                    </dd>
                    <dt>Written off</dt>
                    <dd>
                      <Money value={c.outcome.written_off} currency={c.currency} />
                    </dd>
                  </dl>
                </Card>
              ) : null}
              {c.collection ? (
                <Card title="How this is being recovered" subtitle="A publisher debt is recovered by netting it against your future recognized earnings, oldest first — not from a payout request.">
                  <div className="stack">
                    <div className="grid-auto">
                      <div className="stat">
                        <span className="stat-label">Still outstanding</span>
                        <span className="stat-value">
                          <Money value={c.collection.outstanding} currency={c.currency} />
                        </span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Collected so far</span>
                        <span className="stat-value">
                          <Money value={c.collection.collected} currency={c.currency} />
                        </span>
                      </div>
                    </div>
                    {isZeroDecimal(c.collection.outstanding) ? (
                      <Banner tone="success" title="Nothing left outstanding">This case is fully recovered.</Banner>
                    ) : null}
                    {c.collection.collections.length ? (
                      <Table
                        caption="Each time this case's debt was netted from your earnings"
                        rows={c.collection.collections}
                        rowKey={(e) => e.collection_id}
                        columns={[
                          { key: "at", header: "When", cell: (e) => formatLocal(e.collected_at) },
                          { key: "amt", header: "Amount", numeric: true, cell: (e) => <Money value={e.amount} currency={c.currency} /> },
                          { key: "payout", header: "Payout", cell: (e) => (e.payout_id ? <Link to={`/publisher/payouts/${e.payout_id}`}><code>{e.payout_id.slice(0, 8)}</code></Link> : "—") },
                          { key: "after", header: "Outstanding after", numeric: true, cell: (e) => <Money value={e.outstanding_after} currency={c.currency} /> },
                        ]}
                      />
                    ) : (
                      <p className="muted small">Nothing netted yet.</p>
                    )}
                  </div>
                </Card>
              ) : null}
              <Card title="Appeal">
                {c.appeal.state === "available" ? (
                  <AppealForm caseId={c.case_id} onFiled={() => void qc.invalidateQueries({ queryKey: ["debt-case", caseId] })} />
                ) : c.appeal.state === "under_review" ? (
                  <p>Your appeal is under review{c.appeal.filed_at ? ` (filed ${formatLocal(c.appeal.filed_at)})` : ""}.</p>
                ) : c.appeal.state === "upheld" ? (
                  <p>Your appeal was upheld{c.appeal.resolved_at ? ` on ${formatLocal(c.appeal.resolved_at)}` : ""}.</p>
                ) : c.appeal.state === "denied" ? (
                  <p>Your appeal was denied{c.appeal.resolved_at ? ` on ${formatLocal(c.appeal.resolved_at)}` : ""}.</p>
                ) : (
                  <p className="muted">Nothing to appeal: {c.status === "under_review" ? "the case is still under review." : "the finding wasn't upheld."}</p>
                )}
              </Card>
            </>
          );
        }}
      </QueryView>
    </div>
  );
}

// POST /v1/debt-cases/{id}/appeal (migration 0073, G-44). One open
// appeal at a time: the SQL replies "already_appealed" (still success)
// rather than erroring if a second filing lands while one is open, so a
// stale intent key or a retry after a lost response can't double-file.
function AppealForm({ caseId, onFiled }: { caseId: string; onFiled: () => void }) {
  const ep = useEndpoints();
  const intent = useIntentKey();
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [reasonError, setReasonError] = useState<string | undefined>(undefined);
  const [filed, setFiled] = useState<AppealDebtCaseResponse | null>(null);
  const file = useMutation({
    mutationFn: () =>
      ep.publisher.appealDebtCase(
        caseId,
        { reason: reason.trim(), evidence: evidence.trim() || undefined },
        { idempotencyKey: intent.keyFor(reason.trim() + "\u0000" + evidence.trim()) },
      ),
    onSuccess: (res) => {
      intent.reset();
      setFiled(res);
      onFiled();
    },
  });

  if (filed) {
    return (
      <Banner tone="success" title="Appeal filed" live="polite">
        {filed.result === "already_appealed" ? "This case already has an appeal under review." : "A reviewer will look at it, and you'll see the outcome here."}
      </Banner>
    );
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const r = reason.trim();
    if (!r) {
      setReasonError("Explain why you believe this finding is wrong.");
      return;
    }
    setReasonError(undefined);
    file.mutate();
  }

  return (
    <form className="stack" onSubmit={submit} noValidate>
      <p className="small muted">Tell us why you believe this finding is wrong. A reviewer distinct from the original decision will look at it.</p>
      <Textarea label="Grounds for appeal" value={reason} onChange={(e) => setReason(e.target.value)} rows={4} error={reasonError} />
      <Textarea label="Additional evidence" optional value={evidence} onChange={(e) => setEvidence(e.target.value)} rows={2} />
      {file.isError ? <CommandError error={file.error} /> : null}
      <div>
        <Button type="submit" loading={file.isPending}>
          Submit appeal
        </Button>
      </div>
    </form>
  );
}
